import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Link2, Share2 } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { BookButton, CallButton } from "@/components/Buttons";
import { OfferCard } from "@/components/Offers";
import { OFFERS, SITE_URL, SEDATION_NOTE } from "@/data";
import { useSEO } from "@/hooks/useSEO";
import { track } from "@/hooks/useAnalytics";

const SHARE_URL = `${SITE_URL}/offers`;
const SHARE_TEXT = "Glebe Park Dental, Canberra City — new patient check-up $250, free implant & clear aligner consults.";

const ShareBar = () => {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== "undefined" && !!navigator.share;

  const copy = async () => {
    await navigator.clipboard.writeText(SHARE_URL);
    setCopied(true);
    toast.success("Link copied — paste it into Google Business, Facebook or Instagram.");
    track("offers_share", { method: "copy" });
    setTimeout(() => setCopied(false), 2500);
  };

  const share = async () => {
    try {
      await navigator.share({ title: "Glebe Park Dental — Special Offers", text: SHARE_TEXT, url: SHARE_URL });
      track("offers_share", { method: "native" });
    } catch {
      /* user dismissed */
    }
  };

  return (
    <div data-testid="offers-share-bar" className="flex flex-col gap-4 rounded-2xl border border-stone/80 bg-cream p-6 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">Shareable link</p>
        <p data-testid="offers-share-url" className="mt-1 break-all font-sans text-sm text-cream">{SHARE_URL}</p>
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          onClick={copy}
          data-testid="offers-copy-link-button"
          className="inline-flex items-center gap-2 rounded-md bg-forest px-5 py-2.5 font-sans text-sm font-medium text-cream transition-colors hover:bg-pine"
        >
          {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
          {copied ? "Copied" : "Copy link"}
        </button>
        {canShare && (
          <button onClick={share} data-testid="offers-native-share-button" className="inline-flex items-center gap-2 rounded-full border border-cream/25 px-5 py-2.5 font-sans text-sm text-cream transition-colors hover:bg-cream hover:text-forest">
            <Share2 className="h-4 w-4" /> Share
          </button>
        )}
      </div>
    </div>
  );
};

export default function OffersPage() {
  useSEO({
    title: "Special Offers | Glebe Park Dental, Canberra City",
    description: "New patient check-up $250 (incl. check-up X-rays), free dental implant consult and free clear aligner (Invisalign) consult at Glebe Park Dental, next to Glebe Park in Canberra City. Book online.",
    path: "/offers",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "OfferCatalog",
      name: "Glebe Park Dental — Special Offers",
      url: SHARE_URL,
      itemListElement: OFFERS.map((o) => ({ "@type": "Offer", name: o.title, description: o.text, url: `${SITE_URL}${o.link}` })),
    },
  });

  return (
    <main className="bg-cream" data-testid="offers-page">
      <Nav />
      <section className="bg-sage pb-16 pt-40 lg:pt-44">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <Reveal><p className="font-mono text-xs tracking-[0.3em] uppercase text-leafmute">Special offers</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              A few ways to make the first step <span className="italic text-fern">a bit easier.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-fern sm:text-lg">
              Everyone deserves straight answers before they spend money on their teeth. These offers are for new patients at Glebe Park Dental in Canberra City — book online or ring us, and just mention the offer.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-wrap gap-4">
              <BookButton testid="offers-page-book-button">Book online</BookButton>
              <CallButton testid="offers-page-call-button" />
            </div>
          </Reveal>
          <Reveal delay={0.35} className="mt-12"><ShareBar /></Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {OFFERS.map((o, i) => <OfferCard key={o.id} offer={o} index={i} />)}
          </div>
          <Reveal delay={0.1}>
            <div data-testid="offers-terms" className="mt-12 rounded-2xl border border-ink/10 bg-white p-8">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-leafdeep">The fine print, in plain English</p>
              <ul className="mt-4 space-y-2 font-sans text-sm leading-relaxed text-fern">
                <li>Offers are for new patients of Glebe Park Dental and can't be combined with health fund claims for the same item.</li>
                <li>Free consultations cover the clinical consultation only. Any X-rays, 3D scans or other diagnostic imaging needed to plan treatment may be charged separately — we'll tell you before we take them.</li>
                <li>The $250 new patient check-up includes a clinical examination and standard check-up X-rays. A full-mouth X-ray (OPG) or 3D scan, if clinically needed, may be charged separately.</li>
                <li>Offers may change or end without notice; the version on this page is the current one.</li>
              </ul>
              <p className="mt-6 font-sans text-sm text-fern">{SEDATION_NOTE} <Link to="/services/conscious-sedation" className="font-medium text-leafdeep hover:underline">How sedation works</Link></p>
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </main>
  );
}
